/**
 * Error Store Integration Component
 *
 * Bridges the error store and SnackbarContext to display error notifications.
 * Listens to errors from the error store and dispatches them to the unified
 * notification system.
 *
 * This component replaces the old ErrorNotification component and integrates
 * error store functionality with the enhanced SnackbarContext.
 */

import React, { useEffect, useRef } from 'react';
import { useErrorHandler } from '../../stores/errorStore';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { AppError } from '../../types/errors';

/**
 * Component that integrates error store with SnackbarContext
 * Place this component in your app root (e.g., App.tsx)
 */
export const ErrorStoreIntegration: React.FC = () => {
  const errorHandler = useErrorHandler();
  const { show } = useSnackbar();
  const displayedErrorsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Process errors from the error store
    const processErrors = () => {
      errorHandler.errors.forEach((error: AppError) => {
        // Skip if already displayed
        if (displayedErrorsRef.current.has(error.id)) {
          return;
        }

        // Only show certain error types as notifications
        // Network and auth errors are handled by banners/dialogs
        if (error.type === 'validation' || error.type === 'server') {
          // Dispatch to snackbar
          show({
            message: error.message,
            severity: error.type === 'validation' ? 'warning' : 'error',
            errorType: error.type,
            context: error.context,
            details: error.details,
          });

          // Mark as displayed
          displayedErrorsRef.current.add(error.id);
        }
      });

      // Clean up displayed errors that have been removed from the store
      const currentErrorIds = new Set(errorHandler.errors.map(e => e.id));
      displayedErrorsRef.current.forEach(id => {
        if (!currentErrorIds.has(id)) {
          displayedErrorsRef.current.delete(id);
        }
      });
    };

    processErrors();
  }, [errorHandler.errors, show]);

  // This component doesn't render anything - it's just an integration bridge
  return null;
};
